import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, Save, Trash2, ImagePlus, Eye, EyeOff, GripVertical } from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const MAX_CHARS = 1300;
const REQUIRED_SIZE = 1080;

function InformacaoForm({ onSaved, onCancelar }) {
  const [texto, setTexto] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dims, setDims] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg"].includes(f.type)) {
      toast.error("Apenas PNG ou JPEG");
      return;
    }
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      setDims({ w: img.width, h: img.height });
      setPreview(url);
      setFile(f);
    };
    img.src = url;
  };

  const salvar = async () => {
    if (!file) return toast.error("Selecione uma imagem");
    if (!texto.trim()) return toast.error("Digite o texto informativo");
    if (texto.length > MAX_CHARS) return toast.error(`Texto excede ${MAX_CHARS} caracteres`);
    if (dims && (dims.w !== REQUIRED_SIZE || dims.h !== REQUIRED_SIZE)) {
      if (!window.confirm(`A imagem é ${dims.w}×${dims.h}, o padrão recomendado é ${REQUIRED_SIZE}×${REQUIRED_SIZE}. Deseja publicar mesmo assim?`)) return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("texto", texto);
      fd.append("file", file);
      await api.post("/informacoes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Informação publicada");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const dimOk = !dims || (dims.w === REQUIRED_SIZE && dims.h === REQUIRED_SIZE);

  return (
    <Card className="border-2 border-[#083CA6]">
      <CardHeader><CardTitle className="text-base text-[#083CA6]">Nova Informação</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Imagem (PNG ou JPEG · 1080×1080 px)</Label>
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleFile}
              className="hidden"
              data-testid="info-file"
            />
            <div
              onClick={() => inputRef.current?.click()}
              className="mt-1 border-2 border-dashed border-slate-300 rounded aspect-square flex items-center justify-center cursor-pointer hover:border-[#083CA6] transition-colors overflow-hidden"
              data-testid="info-file-drop"
            >
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-slate-500 p-8">
                  <ImagePlus className="w-8 h-8 mx-auto mb-2" />
                  <div className="text-sm">Clique para selecionar</div>
                  <div className="text-xs mt-1">PNG / JPEG · 1080×1080</div>
                </div>
              )}
            </div>
            {dims && (
              <div className={`mt-2 text-xs ${dimOk ? "text-green-700" : "text-amber-700"}`}>
                {dimOk ? "✓" : "⚠️"} Dimensões: {dims.w}×{dims.h}px
                {!dimOk && ` (recomendado ${REQUIRED_SIZE}×${REQUIRED_SIZE})`}
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="texto" className="text-xs">
              Texto (até {MAX_CHARS} caracteres) — {texto.length}/{MAX_CHARS}
            </Label>
            <Textarea
              id="texto"
              value={texto}
              onChange={(e) => setTexto(e.target.value.slice(0, MAX_CHARS))}
              rows={16}
              placeholder="Escreva o texto informativo que aparecerá abaixo da imagem..."
              className="mt-1 resize-none"
              data-testid="info-texto"
            />
            <div className={`text-xs mt-1 ${texto.length > MAX_CHARS ? "text-red-600" : "text-slate-500"}`}>
              {texto.length > MAX_CHARS ? "Excedeu o limite!" : `Faltam ${MAX_CHARS - texto.length} caracteres`}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={salvar} disabled={saving} className="bg-[#00883A] hover:bg-[#006e2e]" data-testid="btn-salvar-info">
            <Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Publicar Informação"}
          </Button>
          <Button variant="outline" onClick={onCancelar}>Cancelar</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function InformacaoCard({ info, onToggle, onDelete }) {
  const src = `${API}/informacoes/${info.id}/imagem`;
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: info.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
  };
  return (
    <Card ref={setNodeRef} style={style} className={isDragging ? "shadow-2xl ring-2 ring-[#083CA6]" : ""}>
      <CardContent className="p-4">
        <div className="grid md:grid-cols-[auto_200px_1fr_auto] gap-4 items-start">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#083CA6] p-2 self-center touch-none"
            data-testid={`drag-handle-${info.id}`}
            title="Arraste para reordenar"
            aria-label="Arraste para reordenar"
          >
            <GripVertical className="w-5 h-5" />
          </button>
          <img src={src} alt="informação" className="w-full aspect-square object-cover rounded border" />
          <div className="space-y-2">
            <div className="flex gap-2 items-center">
              {info.publicado ? (
                <Badge className="bg-green-100 text-green-800">🟢 Publicado</Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-800">⚫ Rascunho</Badge>
              )}
              <span className="text-xs text-slate-500">{new Date(info.created_at).toLocaleString("pt-BR")}</span>
              <span className="text-xs text-slate-400">· ordem {info.ordem ?? 0}</span>
            </div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-6">{info.texto}</p>
            <div className="text-xs text-slate-400">{info.texto.length} caracteres</div>
          </div>
          <div className="flex flex-col gap-2">
            <Button size="sm" variant="outline" onClick={() => onToggle(info)} data-testid={`btn-toggle-info-${info.id}`}>
              {info.publicado ? <><EyeOff className="w-4 h-4 mr-1" />Ocultar</> : <><Eye className="w-4 h-4 mr-1" />Publicar</>}
            </Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => onDelete(info)} data-testid={`btn-delete-info-${info.id}`}>
              <Trash2 className="w-4 h-4 mr-1" />Excluir
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function InformacoesPage() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    try {
      const r = await api.get("/informacoes");
      setItems(r.data.items);
    } catch (e) {
      logger.error("Falha ao listar informações", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (info) => {
    try {
      await api.put(`/informacoes/${info.id}`, { publicado: !info.publicado });
      toast.success(info.publicado ? "Informação ocultada" : "Informação publicada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const remove = async (info) => {
    if (!window.confirm("Excluir esta informação?")) return;
    try {
      await api.delete(`/informacoes/${info.id}`);
      toast.success("Informação excluída");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);  // optimistic
    try {
      await api.put("/informacoes/reorder/batch", { ids: next.map((i) => i.id) });
      toast.success("Ordem atualizada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao reordenar");
      load(); // reverte
    }
  };

  return (
    <div className="space-y-6" data-testid="informacoes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-[#083CA6]">Informações</h1>
          <p className="text-sm text-slate-600">
            Cards informativos publicados na página inicial · <span className="text-[#083CA6] font-medium">arraste pelo ícone ⋮⋮ para reordenar</span>
          </p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-nova-info">
            <PlusCircle className="w-4 h-4 mr-2" />Criar Informação
          </Button>
        )}
      </div>

      {showForm && (
        <InformacaoForm
          onSaved={() => { setShowForm(false); load(); }}
          onCancelar={() => setShowForm(false)}
        />
      )}

      {items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-slate-500">
            Nenhuma informação cadastrada. Clique em "Criar Informação" para publicar.
          </CardContent>
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3" data-testid="info-sortable-list">
              {items.map((info) => (
                <InformacaoCard key={info.id} info={info} onToggle={toggle} onDelete={remove} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
